/* eslint-disable max-len */
import { Icon } from "@iconify/react";
import { ColDef, GridReadyEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { Dialog, DialogContent, DialogTitle } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  BillingOverride,
  BillingInvoiceRequest,
  BillingReportRequest,
  BillingInvoicePricingModel,
  RenewalBillingFrequency,
  exportBillingInvoice,
  exportBillingReport,
  getBillingReport,
  getBillingReportDetails,
  previewBillingInvoice,
  searchBillingCompanies,
} from "../../api/billing";
import { Input, Label, TextArea } from "../../components/shared/InputField";
import { SearchableSelect } from "../../components/shared/SearchableSelect";
import { Select } from "../../components/shared/Select";
import { SwitchButton } from "../../components/shared/SwitchButton";
import AdvancedAgGridSearch from "../../components/AdvancedAgGridSearch";
import {
  ButtonSize,
  HStack,
  PrimaryCTAButton,
  SecondaryCTAButton,
  TertiaryCTAButton,
  VStack,
} from "../../components/utils";
import { downloadBlobObject } from "../../utils/DownloadFile";
import { formatDisplayDate } from "../../utils/date";

type BillingReport = {
  financialYear: number;
  selectedQuarter: string;
  isBillingAdmin: boolean;
  rule: {
    billingDate: string;
    applyGoLiveFloor: boolean;
    excludeForfeitedFromNetVested: boolean;
    considerOnlyVestedOptions: boolean;
    overrideCount: number;
  };
  summary: {
    beforeSelectedQuarter: number;
    currentQuarterAdditions: number;
    totalBillingQuantity: number;
  };
  companies: Array<{
    companyId: string;
    companyName: string;
    people: number;
    beforeSelectedQuarter: number;
    currentQuarterAdditions: number;
    totalBillingQuantity: number;
    needsReview: number;
  }>;
  reviewItems: Array<{
    companyName: string;
    employeeName: string;
    employeeId: string;
    reason: string;
  }>;
  reviewItemCount: number;
};

type BillingPerson = {
  companyId: string;
  companyName: string;
  employeeId: string;
  employeeName: string;
  employeeEmail: string;
  employeeIdentifier: string;
  employeeStatus: string;
  calculatedBillingStart: string;
  billingStart: string;
  billingSource: string;
  platformGoLiveDate: string;
  goLiveApplied: boolean;
  missingGoLive: boolean;
  override?: { billingStartDate: string; reason: string };
  isBeforeSelectedQuarter: boolean;
  isAddedInSelectedQuarter: boolean;
  backdated: boolean;
  grants: Array<{
    id: string;
    identifier: string;
    state: string;
    candidateBillingDate: string;
    source: string;
    recordCreatedAt: string;
    netVested: number;
  }>;
};

type InvoiceSlabDraft = {
  upperLimit: string;
  annualRate: string;
};

const renewalFrequencyOptions: Array<{
  id: RenewalBillingFrequency;
  name: string;
}> = [
  { id: "YEARLY", name: "Yearly, due in Q4" },
  { id: "HALF_YEARLY", name: "Half yearly, due in Q2 and Q4" },
  { id: "QUARTERLY", name: "Quarterly, due in every quarter" },
];

const pricingModelOptions: Array<{
  id: BillingInvoicePricingModel;
  name: string;
}> = [
  { id: "CAPACITY_SLABS", name: "Full capacity slabs" },
  { id: "INCLUDED_CAPACITY_OVERAGE", name: "Included capacity and overage" },
];

type BillingInvoice = {
  companyId: string;
  companyName: string;
  currency: string;
  financialYear: number;
  selectedQuarter: string;
  renewalFrequency: RenewalBillingFrequency;
  pricingModel: BillingInvoicePricingModel;
  rule: BillingReport["rule"];
  totalQualifyingRoster: number;
  selectedQuarterAdditions: number;
  renewals: number;
  chargedPeople: number;
  totalChargeableMonths: number;
  subtotal: number;
  total: number;
  empty: boolean;
  slabs: Array<{ from: number; upperLimit: number; annualRate: number }>;
  reviewItemCount: number;
  lines: Array<{
    billingType: "RENEWAL" | "ADDITION";
    pricingType: "CAPACITY_SLAB" | "INCLUDED_CAPACITY" | "OVERAGE";
    slabFrom: number;
    slabTo: number;
    capacity: number;
    annualRate: number;
    chargeStart: string;
    chargeableMonths: number;
    amount: number;
    triggerEmployeeId: string;
    triggerEmployeeIdentifier: string;
    triggerEmployeeName: string;
    triggerBillingStart: string;
    backdated: boolean;
    missingGoLive: boolean;
    goLiveApplied: boolean;
    override?: { reason: string };
  }>;
  roster: Array<{
    employeeId: string;
    employeeIdentifier: string;
    employeeName: string;
    billingStart: string;
    seat: number;
    slabFrom: number;
    slabTo: number;
    annualRate: number;
    pricingType?: "CAPACITY_SLAB" | "INCLUDED_CAPACITY" | "OVERAGE";
    isCharged: boolean;
  }>;
};

const statusOptions = ["EMPLOYED", "RESIGNED"];
const quarterOptions = ["Q1", "Q2", "Q3", "Q4"];

function lastCompleteFinancialQuarter() {
  const now = new Date();
  const month = now.getMonth();
  const quarterIndex = Math.floor(((month + 9) % 12) / 3);
  const completedIndex = quarterIndex === 0 ? 3 : quarterIndex - 1;
  const currentFinancialYear =
    month >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  return {
    financialYear:
      quarterIndex === 0 ? currentFinancialYear - 1 : currentFinancialYear,
    quarter: quarterOptions[completedIndex],
  };
}

function number(value: number | undefined) {
  return (value || 0).toLocaleString();
}

function date(value: string) {
  return value ? formatDisplayDate(value) : "Not available";
}

function money(value: number, currency: string) {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value || 0);
  } catch {
    return `${currency || ""} ${(value || 0).toFixed(2)}`.trim();
  }
}

export default function BillingDashboard() {
  const initialPeriod = useMemo(lastCompleteFinancialQuarter, []);
  const [financialYear, setFinancialYear] = useState(
    initialPeriod.financialYear
  );
  const [selectedQuarter, setSelectedQuarter] = useState(initialPeriod.quarter);
  const [companyIds, setCompanyIds] = useState<string[]>([]);
  const [companyResults, setCompanyResults] = useState<
    Array<{ id: string; name: string }>
  >([]);
  const [statuses, setStatuses] = useState<string[]>([]);
  const [applyGoLiveFloor, setApplyGoLiveFloor] = useState(true);
  const [excludeForfeitedFromNetVested, setExcludeForfeitedFromNetVested] =
    useState(true);
  const [considerOnlyVestedOptions, setConsiderOnlyVestedOptions] =
    useState(false);
  const [overrides, setOverrides] = useState<BillingOverride[]>([]);
  const [report, setReport] = useState<BillingReport>();
  const [request, setRequest] = useState<BillingReportRequest>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [expandedEmployee, setExpandedEmployee] = useState<string>();
  const [detailCompany, setDetailCompany] = useState<{
    id: string;
    name: string;
  }>();
  const [details, setDetails] = useState<BillingPerson[]>([]);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [loadingCompanyId, setLoadingCompanyId] = useState("");
  const [exporting, setExporting] = useState(false);
  const [overrideSaving, setOverrideSaving] = useState(false);
  const [overridePerson, setOverridePerson] = useState<BillingPerson>();
  const [overrideDate, setOverrideDate] = useState("");
  const [overrideReason, setOverrideReason] = useState("");
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [invoiceSlabs, setInvoiceSlabs] = useState<InvoiceSlabDraft[]>([
    { upperLimit: "100", annualRate: "" },
  ]);
  const [invoicePreview, setInvoicePreview] = useState<BillingInvoice>();
  const [renewalFrequency, setRenewalFrequency] = useState<
    RenewalBillingFrequency | ""
  >("");
  const [pricingModel, setPricingModel] =
    useState<BillingInvoicePricingModel>("CAPACITY_SLABS");
  const [includedCapacity, setIncludedCapacity] = useState("2000");
  const [includedAnnualRate, setIncludedAnnualRate] = useState("");
  const [overageAnnualRate, setOverageAnnualRate] = useState("");
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [invoiceExporting, setInvoiceExporting] = useState(false);
  const companySearchTimer = useRef<ReturnType<typeof setTimeout>>();

  const buildRequest = (): BillingReportRequest => ({
    financialYear,
    quarters: [selectedQuarter],
    ...(companyIds.length ? { companyIds } : {}),
    ...(statuses.length ? { statuses } : {}),
    applyGoLiveFloor,
    excludeForfeitedFromNetVested,
    considerOnlyVestedOptions,
    ...(overrides.length ? { overrides } : {}),
  });

  const loadReport = async (nextRequest: BillingReportRequest) => {
    setLoading(true);
    setError("");
    try {
      const nextReport = await getBillingReport(nextRequest);
      setReport(nextReport);
      setRequest(nextRequest);
      setDetailCompany(undefined);
      setDetails([]);
      setInvoicePreview(undefined);
      setInvoiceOpen(false);
    } catch (loadError: any) {
      const message =
        loadError?.response?.data?.errorRaw ||
        "We couldn't load the billing report";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const runReport = () => {
    if (companyIds.length !== 1) {
      toast.error("Select one company before running a billing report");
      return;
    }
    loadReport(buildRequest());
  };

  useEffect(() => {
    let active = true;
    searchBillingCompanies("")
      .then((companies) => {
        if (active) setCompanyResults(companies);
      })
      .catch(() => {
        if (active) toast.error("We couldn't load companies");
      });
    return () => {
      active = false;
      if (companySearchTimer.current) clearTimeout(companySearchTimer.current);
    };
  }, []);

  const loadCompanyDetails = async (company: {
    companyId: string;
    companyName: string;
  }) => {
    if (!request) return;
    setDetailsLoading(true);
    setLoadingCompanyId(company.companyId);
    setDetailCompany({ id: company.companyId, name: company.companyName });
    setExpandedEmployee(undefined);
    try {
      const result = await getBillingReportDetails({
        ...request,
        companyId: company.companyId,
      });
      setDetails(result.details);
    } catch (detailError: any) {
      setDetails([]);
      toast.error(
        detailError?.response?.data?.errorRaw ||
          "We couldn't load people for this company"
      );
    } finally {
      setDetailsLoading(false);
      setLoadingCompanyId("");
    }
  };

  const resetRule = () => {
    setApplyGoLiveFloor(true);
    setExcludeForfeitedFromNetVested(true);
    setConsiderOnlyVestedOptions(false);
    setOverrides([]);
  };

  const selectStatus = (status: string) => {
    setStatuses((current) =>
      current.includes(status)
        ? current.filter((value) => value !== status)
        : [...current, status]
    );
  };

  const searchCompanies = (search: string) => {
    if (companySearchTimer.current) clearTimeout(companySearchTimer.current);
    companySearchTimer.current = setTimeout(async () => {
      try {
        const matches: Array<{ id: string; name: string }> =
          await searchBillingCompanies(search);
        setCompanyResults((current) => {
          const selected = current.filter((company) =>
            companyIds.includes(company.id)
          );
          return [
            ...selected,
            ...matches.filter(
              (company) => !selected.some((item) => item.id === company.id)
            ),
          ];
        });
      } catch {
        toast.error("We couldn't find companies");
      }
    }, 250);
  };

  const saveOverride = async () => {
    if (!overridePerson || !overrideDate || !overrideReason.trim()) {
      toast.error("Enter a billing date and a reason");
      return;
    }
    const nextOverrides = [
      ...overrides.filter(
        (override) =>
          override.companyId !== overridePerson.companyId ||
          override.employeeId !== overridePerson.employeeId
      ),
      {
        companyId: overridePerson.companyId,
        employeeId: overridePerson.employeeId,
        billingStartDate: overrideDate,
        reason: overrideReason.trim(),
      },
    ];
    setOverrideSaving(true);
    try {
      setOverrides(nextOverrides);
      const nextRequest = {
        ...buildRequest(),
        overrides: nextOverrides,
      };
      await loadReport(nextRequest);
      setOverridePerson(undefined);
      setOverrideDate("");
      setOverrideReason("");
    } finally {
      setOverrideSaving(false);
    }
  };

  const download = async () => {
    if (!request) return;
    setExporting(true);
    try {
      const response = await exportBillingReport(request);
      downloadBlobObject(
        response.data,
        `billing_${request.financialYear}-${request.financialYear + 1}.xlsx`
      );
    } catch (exportError: any) {
      toast.error(
        exportError?.response?.data?.errorRaw ||
          "We couldn't export the billing report"
      );
    } finally {
      setExporting(false);
    }
  };

  const invoicePayload = (): BillingInvoiceRequest | undefined => {
    if (!request) return undefined;
    if (!renewalFrequency) {
      toast.error("Select a renewal billing frequency");
      return undefined;
    }
    if (pricingModel === "CAPACITY_SLABS") {
      if (
        invoiceSlabs.some(
          (slab) => !slab.upperLimit.trim() || !slab.annualRate.trim()
        )
      ) {
        toast.error("Enter an upper limit and annual rate for every slab");
        return undefined;
      }
    } else if (
      !includedCapacity.trim() ||
      !includedAnnualRate.trim() ||
      !overageAnnualRate.trim()
    ) {
      toast.error(
        "Enter the included capacity, annual rate, and overage annual rate"
      );
      return undefined;
    }
    return {
      ...request,
      renewalFrequency,
      pricingModel,
      ...(pricingModel === "CAPACITY_SLABS"
        ? {
            slabs: invoiceSlabs.map((slab) => ({
              upperLimit: Number(slab.upperLimit),
              annualRate: Number(slab.annualRate),
            })),
          }
        : {
            includedCapacity: Number(includedCapacity),
            includedAnnualRate: Number(includedAnnualRate),
            overageAnnualRate: Number(overageAnnualRate),
          }),
    };
  };

  const previewInvoice = async () => {
    const payload = invoicePayload();
    if (!payload) return;
    setInvoiceLoading(true);
    try {
      const nextPreview = await previewBillingInvoice(payload);
      setInvoicePreview(nextPreview);
    } catch (previewError: any) {
      toast.error(
        previewError?.response?.data?.errorRaw ||
          "We couldn't preview the invoice"
      );
    } finally {
      setInvoiceLoading(false);
    }
  };

  const downloadInvoice = async () => {
    const payload = invoicePayload();
    if (!payload) return;
    setInvoiceExporting(true);
    try {
      const response = await exportBillingInvoice(payload);
      downloadBlobObject(
        response.data,
        `billing_invoice_${payload.financialYear}-${
          payload.financialYear + 1
        }_${selectedQuarter}.xlsx`
      );
    } catch (invoiceError: any) {
      toast.error(
        invoiceError?.response?.data?.errorRaw ||
          "We couldn't export the invoice"
      );
    } finally {
      setInvoiceExporting(false);
    }
  };

  const updateInvoiceSlab = (
    index: number,
    field: keyof InvoiceSlabDraft,
    value: string
  ) => {
    setInvoiceSlabs((current) =>
      current.map((slab, slabIndex) =>
        slabIndex === index ? { ...slab, [field]: value } : slab
      )
    );
    setInvoicePreview(undefined);
  };

  const addInvoiceSlab = () => {
    const previousLimit = Number(
      invoiceSlabs[invoiceSlabs.length - 1]?.upperLimit || 0
    );
    setInvoiceSlabs((current) => [
      ...current,
      { upperLimit: String(previousLimit + 100), annualRate: "" },
    ]);
    setInvoicePreview(undefined);
  };

  const removeInvoiceSlab = (index: number) => {
    setInvoiceSlabs((current) =>
      current.filter((_, slabIndex) => slabIndex !== index)
    );
    setInvoicePreview(undefined);
  };

  const updateRenewalFrequency = (value: RenewalBillingFrequency | "") => {
    setRenewalFrequency(value);
    setInvoicePreview(undefined);
  };

  const updatePricingModel = (value: BillingInvoicePricingModel) => {
    setPricingModel(value);
    setInvoicePreview(undefined);
  };

  return (
    <main>
      <VStack className="w-full gap-6 px-4 py-4 md:px-6">
        <HStack className="w-full flex-wrap items-start justify-between gap-4">
          <VStack className="min-w-0 gap-1">
            <h1 className="text-xl font-semibold leading-7 text-headerColor">
              Billing
            </h1>
            <p className="text-sm text-secondaryText">
              Company billing quantities for Rulezero finance operations
            </p>
          </VStack>
          <HStack className="flex-wrap items-center gap-3">
            <SecondaryCTAButton
              buttonSize={ButtonSize.MEDIUM}
              onClick={download}
              disabled={!request}
              loading={exporting}
              loadingText="Exporting Excel"
            >
              <Icon icon="lucide:download" className="mr-2" width={16} /> Export
              Excel
            </SecondaryCTAButton>
            <PrimaryCTAButton
              buttonSize={ButtonSize.MEDIUM}
              onClick={runReport}
              loading={loading}
              loadingText="Running report"
            >
              <Icon icon="lucide:play" className="mr-2" width={16} /> Run report
            </PrimaryCTAButton>
          </HStack>
        </HStack>

        <section className="rounded-lg border border-borderColor bg-white p-6">
          <VStack className="gap-4">
            <HStack className="flex-wrap items-center justify-between gap-3">
              <VStack className="gap-1">
                <h2 className="text-base font-semibold leading-6 text-headerColor">
                  Report filters
                </h2>
                <p className="text-sm text-secondaryText">
                  Choose a financial year and quarter to see billable people
                  before that quarter, additions in it, and the total.
                </p>
              </VStack>
              <p className="text-xs text-secondaryText">
                Financial year runs April to March
              </p>
            </HStack>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <VStack className="gap-2">
                <Label htmlFor="billing-year">Financial year</Label>
                <Select
                  id="billing-year"
                  value={financialYear}
                  onChange={(event) =>
                    setFinancialYear(Number(event.target.value))
                  }
                  options={[
                    financialYear - 1,
                    financialYear,
                    financialYear + 1,
                  ]}
                  textGetter={(year) => `${year}-${year + 1}`}
                />
              </VStack>
              <VStack className="gap-2">
                <Label htmlFor="billing-quarter">Selected quarter</Label>
                <Select
                  id="billing-quarter"
                  value={selectedQuarter}
                  onChange={(event) => setSelectedQuarter(event.target.value)}
                  options={quarterOptions}
                />
              </VStack>
            </div>
            <VStack className="w-full gap-2 sm:w-96">
              <Label htmlFor="billing-company">Company</Label>
              <SearchableSelect
                id="billing-company"
                onChange={(event) =>
                  setCompanyIds(event.target.value ? [event.target.value] : [])
                }
                onSearchChange={searchCompanies}
                options={companyResults}
                placeholder="Search and select a company"
                textGetter={(company) => company.name}
                value={companyIds[0] || ""}
                valueGetter={(company) => company.id}
              />
              <span className="text-xs text-secondaryText">
                Select one company to run the report
              </span>
            </VStack>
            <HStack className="flex-wrap items-center gap-2">
              <span className="text-xs2 font-medium text-primaryText">
                Status
              </span>
              {statusOptions.map((status) => (
                <TertiaryCTAButton
                  key={status}
                  buttonSize={ButtonSize.SMALL}
                  onClick={() => selectStatus(status)}
                  className={
                    statuses.includes(status) ? "bg-secondary text-white" : ""
                  }
                >
                  {status === "EMPLOYED" ? "Employed" : "Resigned"}
                </TertiaryCTAButton>
              ))}
            </HStack>
            <VStack className="gap-3 border-t border-borderColor pt-4">
              <HStack className="w-full flex-wrap items-start justify-between gap-3">
                <VStack className="gap-1">
                  <h3 className="text-base font-semibold leading-6 text-headerColor">
                    Temporary billing rules
                  </h3>
                  <p className="text-sm text-secondaryText">
                    These apply to the next report, invoice, and export only.
                  </p>
                </VStack>
                <TertiaryCTAButton
                  buttonSize={ButtonSize.SMALL}
                  onClick={resetRule}
                >
                  Reset defaults
                </TertiaryCTAButton>
              </HStack>
              <HStack className="flex-wrap items-center gap-4 text-sm text-primaryText">
                <SwitchButton
                  label="Apply company platform go-live date as the minimum billing date"
                  onClick={() => setApplyGoLiveFloor((current) => !current)}
                  value={applyGoLiveFloor}
                />
                <SwitchButton
                  label="Exclude forfeited options from resigned net vested"
                  onClick={() =>
                    setExcludeForfeitedFromNetVested((current) => !current)
                  }
                  value={excludeForfeitedFromNetVested}
                />
                <SwitchButton
                  label="Calculate resigned eligibility from vested options only"
                  onClick={() =>
                    setConsiderOnlyVestedOptions((current) => !current)
                  }
                  value={considerOnlyVestedOptions}
                />
              </HStack>
            </VStack>
          </VStack>
        </section>

        {error ? (
          <section className="rounded-lg border border-red-200 bg-white p-6">
            <VStack className="gap-3">
              <p className="text-sm text-primaryText">{error}</p>
              <SecondaryCTAButton
                buttonSize={ButtonSize.SMALL}
                onClick={() => request && loadReport(request)}
              >
                Try again
              </SecondaryCTAButton>
            </VStack>
          </section>
        ) : loading && !report ? (
          <section className="rounded-lg border border-borderColor bg-white p-6 text-sm text-secondaryText">
            Loading billing report...
          </section>
        ) : report ? (
          <>
            <section className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <Metric
                label={`Billable before ${report.selectedQuarter}`}
                value={number(report.summary.beforeSelectedQuarter)}
                description="Billing start is before the selected quarter"
              />
              <Metric
                label={`Added in ${report.selectedQuarter}`}
                value={number(report.summary.currentQuarterAdditions)}
                description="Billing start falls in the selected quarter"
              />
              <Metric
                label="Total to bill"
                value={number(report.summary.totalBillingQuantity)}
                description="Billable before the quarter plus additions in it"
              />
            </section>

            <section className="rounded-lg border border-borderColor bg-white p-4 text-sm text-secondaryText">
              <span className="font-medium text-primaryText">
                Applied billing rule:{" "}
              </span>
              Date of grant.{" "}
              {report.rule.applyGoLiveFloor
                ? "Platform go-live floor applied."
                : "Platform go-live floor not applied."}{" "}
              {report.rule.excludeForfeitedFromNetVested
                ? "Forfeited options are excluded from resigned net vested."
                : "Forfeited options are included in resigned net vested."}{" "}
              {report.rule.considerOnlyVestedOptions
                ? "Resigned eligibility uses vested options only."
                : "Resigned eligibility uses net vested options."}{" "}
              {report.rule.overrideCount
                ? `${report.rule.overrideCount} temporary override${
                    report.rule.overrideCount === 1 ? "" : "s"
                  } applied.`
                : ""}
            </section>

            <section className="rounded-lg border border-borderColor bg-white p-6">
              <HStack className="w-full flex-wrap items-start justify-between gap-4">
                <VStack className="min-w-0 gap-1">
                  <h2 className="text-base font-semibold leading-6 text-headerColor">
                    Generate priced invoice
                  </h2>
                  <p className="text-sm text-secondaryText">
                    Enter annual capacity slab rates and choose when renewal
                    billing is due. Pricing inputs and generated invoices are
                    not saved.
                  </p>
                </VStack>
                {!report.isBillingAdmin ? (
                  <p className="text-sm text-secondaryText">
                    Billing admin access is required to enter rates and generate
                    an invoice.
                  </p>
                ) : report.summary.totalBillingQuantity ? (
                  <PrimaryCTAButton
                    buttonSize={ButtonSize.MEDIUM}
                    onClick={() => setInvoiceOpen(true)}
                  >
                    <Icon icon="lucide:file-text" className="mr-2" width={16} />{" "}
                    Generate invoice
                  </PrimaryCTAButton>
                ) : (
                  <p className="text-sm text-secondaryText">
                    There are no renewals or additions to invoice in{" "}
                    {report.selectedQuarter}.
                  </p>
                )}
              </HStack>
            </section>

            <CompanyTable
              companies={report.companies}
              loadingCompanyId={loadingCompanyId}
              onViewPeople={loadCompanyDetails}
            />
            {detailCompany ? (
              <PeopleTable
                companyName={detailCompany.name}
                loading={detailsLoading}
                people={details}
                expandedEmployee={expandedEmployee}
                setExpandedEmployee={setExpandedEmployee}
                canOverride={report.isBillingAdmin}
                onOverride={(person) => {
                  setOverridePerson(person);
                  setOverrideDate(person.billingStart);
                  setOverrideReason(person.override?.reason || "");
                }}
              />
            ) : (
              <section className="rounded-lg border border-borderColor bg-white p-6 text-sm text-secondaryText">
                Choose a company to load its people and grants.
              </section>
            )}
            <ReviewTable items={report.reviewItems} />
          </>
        ) : null}
      </VStack>
      <OverrideDialog
        person={overridePerson}
        billingDate={overrideDate}
        reason={overrideReason}
        saving={overrideSaving}
        setBillingDate={setOverrideDate}
        setReason={setOverrideReason}
        onClose={() => setOverridePerson(undefined)}
        onSave={saveOverride}
      />
      <InvoiceDialog
        open={invoiceOpen}
        onClose={() => setInvoiceOpen(false)}
        slabs={invoiceSlabs}
        preview={invoicePreview}
        previewLoading={invoiceLoading}
        exportLoading={invoiceExporting}
        onSlabChange={updateInvoiceSlab}
        onAddSlab={addInvoiceSlab}
        onRemoveSlab={removeInvoiceSlab}
        onPreview={previewInvoice}
        onExport={downloadInvoice}
        renewalFrequency={renewalFrequency}
        onRenewalFrequencyChange={updateRenewalFrequency}
        pricingModel={pricingModel}
        onPricingModelChange={updatePricingModel}
        includedCapacity={includedCapacity}
        includedAnnualRate={includedAnnualRate}
        overageAnnualRate={overageAnnualRate}
        onIncludedCapacityChange={(value) => {
          setIncludedCapacity(value);
          setInvoicePreview(undefined);
        }}
        onIncludedAnnualRateChange={(value) => {
          setIncludedAnnualRate(value);
          setInvoicePreview(undefined);
        }}
        onOverageAnnualRateChange={(value) => {
          setOverageAnnualRate(value);
          setInvoicePreview(undefined);
        }}
      />
    </main>
  );
}

function Metric({
  label,
  value,
  description,
}: {
  label: string;
  value: string;
  description: string;
}) {
  return (
    <VStack className="min-h-32 gap-2 rounded-lg border border-borderColor bg-white p-4">
      <p className="text-xs2 font-medium text-secondaryText">{label}</p>
      <p className="font-semibold text-2xl text-headerColor tabular-nums">
        {value}
      </p>
      <p className="text-xs text-secondaryText">{description}</p>
    </VStack>
  );
}

function BillingAgGrid({
  tableIdentifier,
  rowData,
  columnDefs,
  height = "h-96",
  getRowId,
}: {
  tableIdentifier: string;
  rowData: any[];
  columnDefs: ColDef<any>[];
  height?: string;
  getRowId?: (params: any) => string;
}) {
  const [gridApi, setGridApi] = useState<any>();
  return (
    <VStack className="gap-3 p-4 pt-0">
      <HStack className="w-full justify-end gap-2">
        {gridApi && (
          <>
            <TertiaryCTAButton
              buttonSize={ButtonSize.SMALL}
              onClick={() => gridApi.setFilterModel(null)}
            >
              Clear filters
            </TertiaryCTAButton>
            <AdvancedAgGridSearch
              gridApi={gridApi}
              tableIdentifier={tableIdentifier}
            />
          </>
        )}
      </HStack>
      <div className={`${height} w-full overflow-x-auto ag-theme-material`}>
        <AgGridReact
          rowData={rowData}
          columnDefs={columnDefs}
          getRowId={getRowId}
          defaultColDef={{
            sortable: true,
            resizable: true,
            filter: true,
            minWidth: 120,
            flex: 1,
          }}
          pagination
          paginationPageSize={25}
          suppressCellFocus
          suppressMenuHide
          animateRows
          onGridReady={(event: GridReadyEvent) => setGridApi(event.api)}
          overlayNoRowsTemplate="<span>No rows match the selected filters.</span>"
        />
      </div>
    </VStack>
  );
}

function CompanyTable({
  companies,
  loadingCompanyId,
  onViewPeople,
}: {
  companies: BillingReport["companies"];
  loadingCompanyId: string;
  onViewPeople: (company: BillingReport["companies"][number]) => void;
}) {
  const columnDefs = useMemo<ColDef<any>[]>(
    () => [
      {
        headerName: "Company",
        field: "companyName",
        filter: "agTextColumnFilter",
        minWidth: 240,
      },
      {
        headerName: "Before quarter",
        field: "beforeSelectedQuarter",
        filter: "agNumberColumnFilter",
        type: "numericColumn",
      },
      {
        headerName: "Added in quarter",
        field: "currentQuarterAdditions",
        filter: "agNumberColumnFilter",
        type: "numericColumn",
      },
      {
        headerName: "Total to bill",
        field: "totalBillingQuantity",
        filter: "agNumberColumnFilter",
        type: "numericColumn",
      },
      {
        headerName: "Needs review",
        field: "needsReview",
        filter: "agNumberColumnFilter",
        type: "numericColumn",
      },
      {
        headerName: "Details",
        sortable: false,
        filter: false,
        maxWidth: 160,
        cellRenderer: (params: any) => (
          <SecondaryCTAButton
            buttonSize={ButtonSize.SMALL}
            loading={loadingCompanyId === params.data.companyId}
            loadingText="Loading people"
            onClick={() => onViewPeople(params.data)}
          >
            View people
          </SecondaryCTAButton>
        ),
      },
    ],
    [loadingCompanyId, onViewPeople]
  );
  return (
    <section className="overflow-hidden rounded-lg border border-borderColor bg-white">
      <VStack className="gap-1 p-6">
        <h2 className="text-base font-semibold leading-6 text-headerColor">
          Company overview
        </h2>
        <p className="text-sm text-secondaryText">
          Use column filters or advanced filters to review the selected company.
        </p>
      </VStack>
      <BillingAgGrid
        tableIdentifier="billing-company-overview"
        rowData={companies}
        columnDefs={columnDefs}
        height="h-80"
        getRowId={(params) => params.data.companyId}
      />
    </section>
  );
}

function PeopleTable({
  companyName,
  loading,
  people,
  expandedEmployee,
  setExpandedEmployee,
  canOverride,
  onOverride,
}: {
  companyName: string;
  loading: boolean;
  people: BillingPerson[];
  expandedEmployee?: string;
  setExpandedEmployee: (id?: string) => void;
  canOverride: boolean;
  onOverride: (person: BillingPerson) => void;
}) {
  const selectedPerson = people.find(
    (person) => `${person.companyId}-${person.employeeId}` === expandedEmployee
  );
  const columnDefs = useMemo<ColDef<any>[]>(
    () => [
      {
        headerName: "Person",
        field: "employeeName",
        filter: "agTextColumnFilter",
        minWidth: 220,
        valueGetter: (params) =>
          `${params.data.employeeName} ${
            params.data.employeeIdentifier || ""
          } ${params.data.employeeEmail || ""}`,
      },
      {
        headerName: "Status",
        field: "employeeStatus",
        filter: "agSetColumnFilter",
      },
      {
        headerName: "Billing start",
        field: "billingStart",
        filter: "agDateColumnFilter",
        valueFormatter: (params) => date(params.value),
      },
      {
        headerName: "Billing group",
        field: "billingGroup",
        filter: "agSetColumnFilter",
        valueGetter: (params) =>
          params.data.isBeforeSelectedQuarter
            ? "Before selected quarter"
            : "Added in selected quarter",
      },
      {
        headerName: "Flags",
        field: "flags",
        filter: "agTextColumnFilter",
        valueGetter: (params) =>
          [
            params.data.backdated && "Backdated",
            params.data.missingGoLive && "Missing go-live",
            params.data.override && "Overridden",
            params.data.goLiveApplied && "Go-live applied",
          ]
            .filter(Boolean)
            .join(", ") || "None",
      },
      {
        headerName: "Actions",
        sortable: false,
        filter: false,
        minWidth: canOverride ? 250 : 140,
        cellRenderer: (params: any) => (
          <HStack className="gap-2">
            <TertiaryCTAButton
              buttonSize={ButtonSize.SMALL}
              onClick={() =>
                setExpandedEmployee(
                  expandedEmployee ===
                    `${params.data.companyId}-${params.data.employeeId}`
                    ? undefined
                    : `${params.data.companyId}-${params.data.employeeId}`
                )
              }
            >
              View grants
            </TertiaryCTAButton>
            {canOverride && (
              <TertiaryCTAButton
                buttonSize={ButtonSize.SMALL}
                onClick={() => onOverride(params.data)}
              >
                Override date
              </TertiaryCTAButton>
            )}
          </HStack>
        ),
      },
    ],
    [canOverride, expandedEmployee, onOverride, setExpandedEmployee]
  );
  return (
    <section className="overflow-hidden rounded-lg border border-borderColor bg-white">
      <VStack className="gap-1 p-6">
        <h2 className="text-base font-semibold leading-6 text-headerColor">
          People and grants
        </h2>
        <p className="text-sm text-secondaryText">
          {loading
            ? "Loading people and grants."
            : `Use advanced filters to review billing records for ${companyName}.`}
        </p>
      </VStack>
      {loading ? (
        <p className="p-6 text-sm text-secondaryText">
          Loading people and grants...
        </p>
      ) : (
        <>
          <BillingAgGrid
            tableIdentifier="billing-people"
            rowData={people}
            columnDefs={columnDefs}
            height="h-[520px]"
            getRowId={(params) =>
              `${params.data.companyId}-${params.data.employeeId}`
            }
          />
          {selectedPerson && (
            <section className="border-t border-borderColor bg-slate-light p-6">
              <GrantDetails person={selectedPerson} />
            </section>
          )}
        </>
      )}
    </section>
  );
}

function Flag({ label }: { label: string }) {
  return (
    <span className="rounded-sm bg-tertiaryBg px-2 py-1 text-xs text-primaryText">
      {label}
    </span>
  );
}

function GrantDetails({ person }: { person: BillingPerson }) {
  const columnDefs = useMemo<ColDef<any>[]>(
    () => [
      {
        headerName: "Grant",
        field: "identifier",
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Current state",
        field: "state",
        filter: "agSetColumnFilter",
      },
      {
        headerName: "Billing date",
        field: "candidateBillingDate",
        filter: "agDateColumnFilter",
        valueFormatter: (params) => date(params.value),
      },
      {
        headerName: "Record created",
        field: "recordCreatedAt",
        filter: "agDateColumnFilter",
        valueFormatter: (params) => date(params.value),
      },
      {
        headerName: "Current eligible options",
        field: "netVested",
        filter: "agNumberColumnFilter",
        type: "numericColumn",
      },
    ],
    []
  );
  return (
    <VStack className="gap-3">
      <HStack className="flex-wrap gap-6 text-xs text-secondaryText">
        <span>Calculated start: {date(person.calculatedBillingStart)}</span>
        <span>Platform go-live: {date(person.platformGoLiveDate)}</span>
        {person.override && <span>Reason: {person.override.reason}</span>}
      </HStack>
      <BillingAgGrid
        tableIdentifier="billing-grants"
        rowData={person.grants}
        columnDefs={columnDefs}
        height="h-64"
        getRowId={(params) => params.data.id}
      />
    </VStack>
  );
}

function ReviewTable({ items }: { items: BillingReport["reviewItems"] }) {
  const columnDefs = useMemo<ColDef<any>[]>(
    () => [
      {
        headerName: "Company",
        field: "companyName",
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Person",
        field: "employeeName",
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Reason",
        field: "reason",
        filter: "agTextColumnFilter",
        minWidth: 360,
      },
    ],
    []
  );
  return (
    <section className="overflow-hidden rounded-lg border border-borderColor bg-white">
      <VStack className="gap-1 p-6">
        <h2 className="text-base font-semibold leading-6 text-headerColor">
          Needs review
        </h2>
        <p className="text-sm text-secondaryText">
          Incomplete historical data that should be checked before relying on
          it.
        </p>
      </VStack>
      <BillingAgGrid
        tableIdentifier="billing-review"
        rowData={items}
        columnDefs={columnDefs}
        height="h-64"
        getRowId={(params) => `${params.data.employeeId}-${params.data.reason}`}
      />
    </section>
  );
}

function InvoiceDialog({
  open,
  onClose,
  slabs,
  preview,
  previewLoading,
  exportLoading,
  onSlabChange,
  onAddSlab,
  onRemoveSlab,
  onPreview,
  onExport,
  renewalFrequency,
  onRenewalFrequencyChange,
  pricingModel,
  onPricingModelChange,
  includedCapacity,
  includedAnnualRate,
  overageAnnualRate,
  onIncludedCapacityChange,
  onIncludedAnnualRateChange,
  onOverageAnnualRateChange,
}: {
  open: boolean;
  onClose: () => void;
  slabs: InvoiceSlabDraft[];
  preview?: BillingInvoice;
  previewLoading: boolean;
  exportLoading: boolean;
  onSlabChange: (
    index: number,
    field: keyof InvoiceSlabDraft,
    value: string
  ) => void;
  onAddSlab: () => void;
  onRemoveSlab: (index: number) => void;
  onPreview: () => void;
  onExport: () => void;
  renewalFrequency: RenewalBillingFrequency | "";
  onRenewalFrequencyChange: (value: RenewalBillingFrequency | "") => void;
  pricingModel: BillingInvoicePricingModel;
  onPricingModelChange: (value: BillingInvoicePricingModel) => void;
  includedCapacity: string;
  includedAnnualRate: string;
  overageAnnualRate: string;
  onIncludedCapacityChange: (value: string) => void;
  onIncludedAnnualRateChange: (value: string) => void;
  onOverageAnnualRateChange: (value: string) => void;
}) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>Generate priced invoice</DialogTitle>
      <DialogContent>
        <VStack className="gap-6 pt-2">
          <VStack className="gap-1">
            <p className="text-sm text-secondaryText">
              Enter annual capacity slab rates. Seats are assigned from the
              earliest billing start date, and an activated slab bills its full
              capacity. These inputs are available only while this page stays
              open.
            </p>
            <p className="text-xs text-secondaryText">
              New additions are charged from their billing month when it starts
              before the 15th. Billing starts on the 15th or later are charged
              from the next month. The invoice does not save or prevent a
              duplicate export.
            </p>
          </VStack>

          <VStack className="gap-2 sm:w-96">
            <Label htmlFor="renewal-frequency">Renewal billing frequency</Label>
            <Select
              id="renewal-frequency"
              value={renewalFrequency}
              placeholder="Select renewal frequency"
              onChange={(event) =>
                onRenewalFrequencyChange(
                  event.target.value as RenewalBillingFrequency | ""
                )
              }
              options={renewalFrequencyOptions}
              textGetter={(option) => option.name}
              valueGetter={(option) => option.id}
            />
            <span className="text-xs text-secondaryText">
              Yearly renewals are due in Q4. Half yearly renewals are due in Q2
              and Q4. Quarterly renewals are due in every quarter.
            </span>
          </VStack>

          <VStack className="gap-2 sm:w-96">
            <Label htmlFor="pricing-model">Pricing calculation</Label>
            <Select
              id="pricing-model"
              value={pricingModel}
              onChange={(event) =>
                onPricingModelChange(
                  event.target.value as BillingInvoicePricingModel
                )
              }
              options={pricingModelOptions}
              textGetter={(option) => option.name}
              valueGetter={(option) => option.id}
            />
            <span className="text-xs text-secondaryText">
              Choose full capacity ranges, or one included range followed by a
              per-person overage rate.
            </span>
          </VStack>

          {pricingModel === "CAPACITY_SLABS" ? (
            <section className="rounded-lg border border-borderColor p-4">
              <VStack className="gap-4">
                <HStack className="w-full flex-wrap items-center justify-between gap-3">
                  <VStack className="gap-1">
                    <h3 className="text-base font-semibold text-headerColor">
                      Annual pricing slabs
                    </h3>
                    <p className="text-xs text-secondaryText">
                      Each slab continues from the prior one. An upper limit of
                      100 covers seats 1 through 100.
                    </p>
                  </VStack>
                  <TertiaryCTAButton
                    buttonSize={ButtonSize.SMALL}
                    onClick={onAddSlab}
                  >
                    <Icon icon="lucide:plus" className="mr-1" width={14} /> Add
                    slab
                  </TertiaryCTAButton>
                </HStack>
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[620px] text-sm">
                    <thead className="text-left text-xs2 text-secondaryText">
                      <tr>
                        <th className="pb-2 font-medium">Seats from</th>
                        <th className="pb-2 font-medium">Seats to</th>
                        <th className="pb-2 font-medium">Annual rate</th>
                        <th className="pb-2 font-medium">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {slabs.map((slab, index) => {
                        const previousLimit = index
                          ? Number(slabs[index - 1].upperLimit)
                          : 0;
                        const from = Number.isFinite(previousLimit)
                          ? previousLimit + 1
                          : "";
                        return (
                          <tr
                            key={index}
                            className="border-t border-borderColor"
                          >
                            <td className="py-3 text-primaryText">
                              {from || "Enter prior limit"}
                            </td>
                            <td className="py-3 pr-3">
                              <VStack className="gap-1">
                                <Label htmlFor={`invoice-slab-limit-${index}`}>
                                  Inclusive upper limit
                                </Label>
                                <Input
                                  id={`invoice-slab-limit-${index}`}
                                  min="1"
                                  type="number"
                                  value={slab.upperLimit}
                                  onChange={(event) =>
                                    onSlabChange(
                                      index,
                                      "upperLimit",
                                      event.target.value
                                    )
                                  }
                                />
                              </VStack>
                            </td>
                            <td className="py-3 pr-3">
                              <VStack className="gap-1">
                                <Label htmlFor={`invoice-slab-rate-${index}`}>
                                  Annual rate
                                </Label>
                                <Input
                                  id={`invoice-slab-rate-${index}`}
                                  min="0"
                                  step="0.01"
                                  type="number"
                                  value={slab.annualRate}
                                  onChange={(event) =>
                                    onSlabChange(
                                      index,
                                      "annualRate",
                                      event.target.value
                                    )
                                  }
                                />
                              </VStack>
                            </td>
                            <td className="py-3">
                              <TertiaryCTAButton
                                buttonSize={ButtonSize.SMALL}
                                disabled={slabs.length === 1}
                                onClick={() => onRemoveSlab(index)}
                              >
                                Remove
                              </TertiaryCTAButton>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </VStack>
            </section>
          ) : (
            <section className="rounded-lg border border-borderColor p-4">
              <VStack className="gap-4">
                <VStack className="gap-1">
                  <h3 className="text-base font-semibold text-headerColor">
                    Included capacity and overage
                  </h3>
                  <p className="text-xs text-secondaryText">
                    Every included seat uses the included annual rate. Every
                    seat after that range uses the per-person annual overage
                    rate.
                  </p>
                </VStack>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <VStack className="gap-2">
                    <Label htmlFor="included-capacity">
                      Included employees
                    </Label>
                    <Input
                      id="included-capacity"
                      min="1"
                      type="number"
                      value={includedCapacity}
                      onChange={(event) =>
                        onIncludedCapacityChange(event.target.value)
                      }
                    />
                  </VStack>
                  <VStack className="gap-2">
                    <Label htmlFor="included-annual-rate">
                      Included annual rate per employee
                    </Label>
                    <Input
                      id="included-annual-rate"
                      min="0"
                      step="0.01"
                      type="number"
                      value={includedAnnualRate}
                      onChange={(event) =>
                        onIncludedAnnualRateChange(event.target.value)
                      }
                    />
                  </VStack>
                  <VStack className="gap-2">
                    <Label htmlFor="overage-annual-rate">
                      Overage annual rate per employee
                    </Label>
                    <Input
                      id="overage-annual-rate"
                      min="0"
                      step="0.01"
                      type="number"
                      value={overageAnnualRate}
                      onChange={(event) =>
                        onOverageAnnualRateChange(event.target.value)
                      }
                    />
                  </VStack>
                </div>
              </VStack>
            </section>
          )}

          {preview &&
            (preview.empty ? (
              <section className="rounded-lg border border-borderColor bg-slate-light p-4 text-sm text-secondaryText">
                There are no renewal or new capacity charges due in{" "}
                {preview.selectedQuarter}.
              </section>
            ) : (
              <InvoicePreview preview={preview} />
            ))}

          <HStack className="w-full flex-wrap justify-end gap-3">
            <TertiaryCTAButton buttonSize={ButtonSize.MEDIUM} onClick={onClose}>
              Close
            </TertiaryCTAButton>
            <SecondaryCTAButton
              buttonSize={ButtonSize.MEDIUM}
              disabled={!preview || preview.empty}
              loading={exportLoading}
              loadingText="Exporting invoice"
              onClick={onExport}
            >
              <Icon icon="lucide:download" className="mr-2" width={16} /> Export
              invoice
            </SecondaryCTAButton>
            <PrimaryCTAButton
              buttonSize={ButtonSize.MEDIUM}
              loading={previewLoading}
              loadingText="Calculating invoice"
              onClick={onPreview}
            >
              <Icon icon="lucide:calculator" className="mr-2" width={16} />{" "}
              Preview invoice
            </PrimaryCTAButton>
          </HStack>
        </VStack>
      </DialogContent>
    </Dialog>
  );
}

function InvoicePreview({ preview }: { preview: BillingInvoice }) {
  const chargeColumns = useMemo<ColDef<any>[]>(
    () => [
      {
        headerName: "Billing type",
        field: "billingType",
        filter: "agSetColumnFilter",
        valueFormatter: (params) =>
          params.value === "RENEWAL" ? "Renewal" : "New capacity",
      },
      {
        headerName: "Trigger person",
        field: "triggerEmployeeName",
        filter: "agTextColumnFilter",
        minWidth: 200,
      },
      {
        headerName: "Trigger billing start",
        field: "triggerBillingStart",
        filter: "agDateColumnFilter",
        valueFormatter: (params) => date(params.value),
      },
      {
        headerName: "Charges from",
        field: "chargeStart",
        filter: "agDateColumnFilter",
        valueFormatter: (params) => date(params.value),
      },
      {
        headerName: "Capacity",
        field: "capacity",
        filter: "agNumberColumnFilter",
        type: "numericColumn",
      },
      {
        headerName: "Slab",
        field: "slab",
        filter: "agTextColumnFilter",
        valueGetter: (params) =>
          `${params.data.slabFrom}-${params.data.slabTo}`,
      },
      {
        headerName: "Annual rate",
        field: "annualRate",
        filter: "agNumberColumnFilter",
        type: "numericColumn",
        valueFormatter: (params) => money(params.value, preview.currency),
      },
      {
        headerName: "Months",
        field: "chargeableMonths",
        filter: "agNumberColumnFilter",
        type: "numericColumn",
      },
      {
        headerName: "Amount",
        field: "amount",
        filter: "agNumberColumnFilter",
        type: "numericColumn",
        valueFormatter: (params) => money(params.value, preview.currency),
      },
    ],
    [preview.currency]
  );
  const rosterColumns = useMemo<ColDef<any>[]>(
    () => [
      {
        headerName: "Person",
        field: "employeeName",
        filter: "agTextColumnFilter",
        minWidth: 220,
        valueGetter: (params) =>
          `${params.data.employeeName} ${params.data.employeeIdentifier || ""}`,
      },
      {
        headerName: "Billing start",
        field: "billingStart",
        filter: "agDateColumnFilter",
        valueFormatter: (params) => date(params.value),
      },
      {
        headerName: "Seat",
        field: "seat",
        filter: "agNumberColumnFilter",
        type: "numericColumn",
      },
      {
        headerName: "Slab",
        field: "slab",
        filter: "agTextColumnFilter",
        valueGetter: (params) =>
          `${params.data.slabFrom}-${params.data.slabTo}`,
      },
      {
        headerName: "Annual rate",
        field: "annualRate",
        filter: "agNumberColumnFilter",
        type: "numericColumn",
        valueFormatter: (params) => money(params.value, preview.currency),
      },
      {
        headerName: "Charged now",
        field: "isCharged",
        filter: "agSetColumnFilter",
        valueFormatter: (params) => (params.value ? "Yes" : "No"),
      },
    ],
    [preview.currency]
  );
  return (
    <VStack className="gap-4">
      <section className="rounded-lg border border-borderColor bg-slate-light p-4">
        <HStack className="w-full flex-wrap items-start justify-between gap-4">
          <VStack className="gap-1">
            <h3 className="text-base font-semibold text-headerColor">
              Invoice preview
            </h3>
            <p className="text-sm text-secondaryText">
              {preview.companyName} · FY {preview.financialYear}-
              {preview.financialYear + 1} · {preview.selectedQuarter}
            </p>
          </VStack>
          <span className="text-sm font-semibold text-headerColor">
            {preview.currency}
          </span>
        </HStack>
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
          <Metric
            label="Qualifying roster"
            value={number(preview.totalQualifyingRoster)}
            description="People assigned a slab seat"
          />
          <Metric
            label="Renewals due"
            value={number(preview.renewals)}
            description={preview.renewalFrequency
              .replace("_", " ")
              .toLowerCase()}
          />
          <Metric
            label="Quarter additions"
            value={number(preview.selectedQuarterAdditions)}
            description="People added in the selected quarter"
          />
          <Metric
            label="Billed seat months"
            value={number(preview.totalChargeableMonths)}
            description="Capacity months in billed slabs"
          />
          <Metric
            label="Total"
            value={money(preview.total, preview.currency)}
            description="No tax or payment terms"
          />
        </div>
      </section>
      <section className="overflow-hidden rounded-lg border border-borderColor bg-white">
        <VStack className="gap-1 p-4">
          <h3 className="text-base font-semibold text-headerColor">
            Invoice charges
          </h3>
          <p className="text-sm text-secondaryText">
            Use advanced filters to inspect pricing, charge periods, and
            amounts.
          </p>
        </VStack>
        <BillingAgGrid
          tableIdentifier="billing-invoice-charges"
          rowData={preview.lines}
          columnDefs={chargeColumns}
          height="h-96"
          getRowId={(params) =>
            `${params.data.billingType}-${params.data.slabFrom}-${params.data.triggerEmployeeId}`
          }
        />
      </section>
      <section className="overflow-hidden rounded-lg border border-borderColor bg-white">
        <VStack className="gap-1 p-4">
          <h3 className="text-base font-semibold text-headerColor">
            Full slab allocation
          </h3>
          <p className="text-sm text-secondaryText">
            Earlier billing starts occupy lower seats. This includes people who
            are not charged in this invoice.
          </p>
        </VStack>
        <BillingAgGrid
          tableIdentifier="billing-invoice-roster"
          rowData={preview.roster}
          columnDefs={rosterColumns}
          height="h-96"
          getRowId={(params) => params.data.employeeId}
        />
      </section>
    </VStack>
  );
}

function OverrideDialog({
  person,
  billingDate,
  reason,
  saving,
  setBillingDate,
  setReason,
  onClose,
  onSave,
}: {
  person?: BillingPerson;
  billingDate: string;
  reason: string;
  saving: boolean;
  setBillingDate: (value: string) => void;
  setReason: (value: string) => void;
  onClose: () => void;
  onSave: () => void;
}) {
  return (
    <Dialog
      open={Boolean(person)}
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Override billing date</DialogTitle>
      <DialogContent>
        <VStack className="gap-4 pt-2">
          <p className="text-sm text-secondaryText">
            This changes the billing allocation for {person?.employeeName} at{" "}
            {person?.companyName} in the current report only.
          </p>
          <VStack className="gap-2">
            <Label htmlFor="override-date">Billing start date</Label>
            <Input
              id="override-date"
              type="date"
              value={billingDate}
              disabled={saving}
              onChange={(event) => setBillingDate(event.target.value)}
            />
          </VStack>
          <VStack className="gap-2">
            <Label htmlFor="override-reason">Reason</Label>
            <TextArea
              id="override-reason"
              rows={3}
              disabled={saving}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          </VStack>
          <HStack className="justify-end gap-3">
            <TertiaryCTAButton
              buttonSize={ButtonSize.MEDIUM}
              disabled={saving}
              onClick={onClose}
            >
              Cancel
            </TertiaryCTAButton>
            <PrimaryCTAButton
              buttonSize={ButtonSize.MEDIUM}
              loading={saving}
              loadingText="Applying override"
              onClick={onSave}
            >
              Apply override
            </PrimaryCTAButton>
          </HStack>
        </VStack>
      </DialogContent>
    </Dialog>
  );
}
